import { Gift } from "lucide-react"
import { useId, useState } from "react"
import { useTranslation } from "react-i18next"

import { DropPickerDialog } from "@/components/missions/drop-picker-dialog"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import type { Locale } from "@/types"
import type { CollabDetail } from "@/types/collab"

import { OfferList } from "./offer-list"

/**
 * OKS-1040 (UX §2.1) — the post-grant « Choisissez votre produit » block.
 *
 * Rendered FIRST in the gifting section, above the address block, iff the
 * server says the choice is outstanding (`product_choice_required`). It is not
 * a gate: the address block stays live beside it (PRD R1b, UQ2).
 *
 * The offer is the OKS-990 one — the same read-only `OfferList` and the same
 * `DropPickerDialog`, in `post_grant` mode (UQ1: a second implementation of the
 * offer is how two surfaces come to describe one product two ways).
 *
 * On success the server stops sending `product_choice_required`, so this block
 * unmounts and the product block takes its place (UX §4: focus goes to the
 * product block heading, which the gifting section owns).
 */
export function ProductChoiceBlock({
  collab,
  locale,
}: {
  collab: CollabDetail
  locale: Locale
}) {
  const { t } = useTranslation()
  const titleId = useId()
  const [open, setOpen] = useState(false)
  const offer = collab.offer ?? []

  return (
    <Card aria-labelledby={titleId} data-testid="gifting-product-choice-block">
      <CardHeader>
        <CardTitle id={titleId} className="flex items-center gap-2 text-base">
          <Gift className="size-5 shrink-0" aria-hidden="true" />
          {t("creator_mission_detail.gifting.product_choice.title")}
        </CardTitle>
        <CardDescription className="break-words">
          {t("creator_mission_detail.gifting.product_choice.body", {
            brand: collab.brand.name,
          })}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <OfferList offer={offer} locale={locale} />
        <Button
          className="min-h-11 w-full"
          onClick={() => setOpen(true)}
          aria-label={t(
            "creator_mission_detail.gifting.product_choice.cta_aria",
            { title: collab.mission.title },
          )}
          data-testid="gifting-product-choice-cta"
        >
          {t("creator_mission_detail.gifting.product_choice.cta")}
        </Button>
      </CardContent>

      <DropPickerDialog
        mode="post_grant"
        open={open}
        onOpenChange={setOpen}
        collabId={collab.id}
        missionTitle={collab.mission.title}
        brandName={collab.brand.name}
        offer={offer}
        locale={locale}
      />
    </Card>
  )
}

/**
 * OKS-1040 (UX §2.1, R3b) — under a pick the BRAND made: « Choisi par {brand}
 * — modifiable jusqu'à l'expédition. » and « Modifier », which re-opens the
 * same picker pre-selected on the brand's choice. Saving flips the source to
 * `creator`; the server then stops sending `product_choice` and this line
 * disappears.
 *
 * Rendered only while `collab.product_choice` is present (brand source,
 * `to_ship`) — the server's answer, never re-derived here.
 */
export function BrandChoiceLine({
  collab,
  locale,
}: {
  collab: CollabDetail
  locale: Locale
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const choice = collab.product_choice
  if (!choice) return null

  // OKS-1042 F2 — ONE predicate, two consumers. The provenance sentence
  // promised "modifiable jusqu'à l'expédition" unconditionally while the
  // « Modifier » affordance below was gated, so on a sole-product drop the
  // creator was promised an edit they could not make. Deriving both from the
  // same expression is the fix: the sentence cannot out-promise the button
  // because there is no second predicate to drift from.
  // ⚠️ Deliberately a narrowed VALUE, not a boolean. `Boolean(...)` severs
  // TypeScript's narrowing of `collab.offer`, which `npm run check` caught at
  // the `offer={...}` prop below — the gate earning its keep on the same day
  // it was found to have been checking zero files.
  const editOffer = choice.editable ? (collab.offer ?? null) : null

  return (
    <div
      className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm"
      data-testid="gifting-product-choice-source-brand"
    >
      <span className="text-muted-foreground break-words">
        {editOffer
          ? t(
              "creator_mission_detail.gifting.product_choice.source_brand_editable",
              { brand: choice.brand_name },
            )
          : t("creator_mission_detail.gifting.product_choice.source_brand", {
              brand: choice.brand_name,
            })}
      </span>
      {editOffer && (
        <>
          <Button
            variant="link"
            className="h-auto min-h-11 p-0"
            onClick={() => setOpen(true)}
            aria-label={t(
              "creator_mission_detail.gifting.product_choice.edit_aria",
              { brand: choice.brand_name },
            )}
            data-testid="gifting-product-choice-edit"
          >
            {t("creator_mission_detail.gifting.product_choice.edit")}
          </Button>
          <DropPickerDialog
            mode="post_grant"
            open={open}
            onOpenChange={setOpen}
            collabId={collab.id}
            missionTitle={collab.mission.title}
            brandName={collab.brand.name}
            offer={editOffer}
            locale={locale}
            initialProductId={choice.chosen_product_id}
            initialVariantId={choice.chosen_variant_id}
          />
        </>
      )}
    </div>
  )
}
